import type { EstudioProveedorIa } from './types';
import { LIMITE_CORTESIA_IA } from './types';
import { puedeUsarCortesia, restanteCortesia } from './cuota';

export interface RutaIaInput {
  preferido?: EstudioProveedorIa | null;
  usadas: number;
  limite?: number;
  hasOpenaiKey?: boolean;
  hasGeminiKey?: boolean;
}

export interface RutaIa {
  provider: EstudioProveedorIa;
  /** True only when billing Clientelo's shared Gemini/OpenAI key. */
  consumeCortesiaClientelo: boolean;
  sinLimiteClientelo: boolean;
  boton: string;
  estado: string;
}

function ownKeyProvider(input: RutaIaInput): 'openai_propia' | 'gemini_propia' | null {
  if (input.hasGeminiKey) return 'gemini_propia';
  if (input.hasOpenaiKey) return 'openai_propia';
  return null;
}

/**
 * Las 200 imágenes al mes (GPT Image 2.5, calidad media) las paga Clientelo.
 * Una clave propia o ChatGPT del comerciante no entra en ese cupo.
 */
export function resolverRutaIa(input: RutaIaInput): RutaIa {
  const limite = input.limite ?? LIMITE_CORTESIA_IA;
  const left = restanteCortesia(input.usadas, limite);
  const preferido = input.preferido || 'cortesia';

  if (preferido === 'openai_propia' || preferido === 'gemini_propia') {
    const label = preferido === 'gemini_propia' ? 'Gemini' : 'OpenAI';
    return {
      provider: preferido,
      consumeCortesiaClientelo: false,
      sinLimiteClientelo: true,
      boton: `Generar con tu ${label} (sin límite de Clientelo)`,
      estado: `Tu clave ${label}: Clientelo no te limita. Solo aplica el cupo o saldo de tu proveedor.`,
    };
  }

  if (preferido === 'chatgpt') {
    return {
      provider: 'chatgpt',
      consumeCortesiaClientelo: false,
      sinLimiteClientelo: true,
      boton: 'Copiar prompt para ChatGPT',
      estado: 'ChatGPT con tu cuenta: sin límite de Clientelo. Te rige el plan de OpenAI (Free, Plus, API, etc.).',
    };
  }

  if (puedeUsarCortesia(input.usadas, limite)) {
    return {
      provider: 'cortesia',
      consumeCortesiaClientelo: true,
      sinLimiteClientelo: false,
      boton: `Generar (quedan ${left} de ${limite})`,
      estado: `Te quedan ${left} de ${limite} imágenes este mes. GPT Image 2.5, calidad media. Las paga Clientelo.`,
    };
  }

  const own = ownKeyProvider(input);
  if (own) {
    const label = own === 'gemini_propia' ? 'Gemini' : 'OpenAI';
    return {
      provider: own,
      consumeCortesiaClientelo: false,
      sinLimiteClientelo: true,
      boton: `Generar con tu ${label} (sin límite de Clientelo)`,
      estado: `Cupo del mes agotado. Pasamos a tu clave ${label}: Clientelo no cuenta más imágenes; solo el límite de tu proveedor.`,
    };
  }

  return {
    provider: 'chatgpt',
    consumeCortesiaClientelo: false,
    sinLimiteClientelo: true,
    boton: 'Continuar en ChatGPT (tu cuenta, sin límite de Clientelo)',
    estado: `Cupo del mes agotado (${limite}/${limite}). Conecta tu clave o usa ChatGPT: Clientelo no te limita.`,
  };
}
